import React from "react";
import EntityForm from "../../EntityForm/EntityForm";
import { entityToFormValuesMapper } from "../../../Common/utils";
import { RESTRICTION_TYPE, WEEK_DAYS } from "../../../Common/constants";
import { defineMessages, injectIntl } from "react-intl";
import moment from "moment";

const messages = defineMessages({
  bothOrNone: {
    id: "app.entityForm.bothOrNoneHours",
    description: "Both range start and range end must be selected",
    defaultMessage: "Both range start and range end must be selected",
  },
  mustSetDateRange: {
    id: "app.entityForm.mustSetDateRange",
    description: "Date range must be set",
    defaultMessage: "Date range must be set",
  },
  startAfterEnd: {
    id: "app.closeBookingWindow.startAfterEnd",
    description: "Start date must be before end date",
    defaultMessage: "Start date must be before end date",
  },
  overlappingDates: {
    id: "app.closeBookingWindow.overlappingDates",
    description: "Overlapping close booking window dates",
    defaultMessage:
      "The selected dates overlap with an existing close booking window ({start} – {end}). Please choose a non-overlapping date range.",
  },
  noOverlapWithBookingWindow: {
    id: "app.closeBookingWindow.noOverlapWithBookingWindow",
    description: "Exclusion range does not overlap any open booking window",
    defaultMessage:
      "The exclusion range ({start} – {end}) does not overlap with any active Booking Window. This exclusion will have no effect.",
  },
});

function customEntityToFormValuesMapper({ entity, fields, locales, translationConfig }) {
  const formValues = entityToFormValuesMapper({
    entity,
    fields,
    locales,
    translationConfig,
  });

  const existingCloseBooking =
    formValues.restrictions &&
    formValues.restrictions[RESTRICTION_TYPE.CLOSE_BOOKING] &&
    formValues.restrictions[RESTRICTION_TYPE.CLOSE_BOOKING].length > 0;

  if (existingCloseBooking) {
    formValues.restrictions = {
      ...formValues.restrictions,
      [RESTRICTION_TYPE.CLOSE_BOOKING]: formValues.restrictions[
        RESTRICTION_TYPE.CLOSE_BOOKING
      ].map(item => ({
        ...item,
        weekDays: Array.isArray(item.weekDays) ? [...item.weekDays] : [],
      })),
    };
  } else {
    formValues.restrictions = {
      [RESTRICTION_TYPE.CLOSE_BOOKING]: [
        {
          startDate: null,
          endDate: null,
          weekDays: Object.values(WEEK_DAYS),
          startHour: null,
          endHour: null,
          timeZone: "Europe/Madrid",
        },
      ],
    };
  }

  return {
    ...formValues,
  };
}

class PromotionsCloseBookingWindowForm extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {};
  }

  componentDidMount() {}

  componentWillUnmount() {}

  getFields(fields) {
    return fields && fields.form;
  }

  handleSubmit = ({ formValues }) => {
    const { id, ...otherFormValues } = formValues;
    this.props.onSubmitEntity({ entity: otherFormValues });
  };

  customValidate = formValues => {
    const restriction = formValues.restrictions[RESTRICTION_TYPE.CLOSE_BOOKING][0];

    if (!restriction.startDate || !restriction.endDate) {
      return this.props.intl.formatMessage(messages.mustSetDateRange);
    }

    if (moment(restriction.startDate).isAfter(moment(restriction.endDate))) {
      return this.props.intl.formatMessage(messages.startAfterEnd);
    }

    if (restriction.startHour && !restriction.endHour) {
      return this.props.intl.formatMessage(messages.bothOrNone);
    }

    const allEntities = this.props.allEntities || [];
    const currentTempId = this.props.entity && this.props.entity._tempId;

    for (const existingEntity of allEntities) {
      if (currentTempId !== undefined && existingEntity._tempId === currentTempId) {
        continue;
      }

      const existing =
        existingEntity.restrictions &&
        existingEntity.restrictions[RESTRICTION_TYPE.CLOSE_BOOKING] &&
        existingEntity.restrictions[RESTRICTION_TYPE.CLOSE_BOOKING][0];

      if (!existing || !existing.startDate || !existing.endDate) {
        continue;
      }

      const overlaps =
        moment(restriction.startDate).isSameOrBefore(moment(existing.endDate)) &&
        moment(existing.startDate).isSameOrBefore(moment(restriction.endDate));

      if (overlaps) {
        return this.props.intl.formatMessage(messages.overlappingDates, {
          start: existing.startDate,
          end: existing.endDate,
        });
      }
    }

    // 4. The exclusion range must overlap with at least one open booking window
    const bookingWindows =
      this.props.parentFormValues && this.props.parentFormValues.bookingWindow;

    if (Array.isArray(bookingWindows) && bookingWindows.length > 0) {
      const overlapsWithAny = bookingWindows.some(bw => {
        const openBooking =
          bw.restrictions &&
          bw.restrictions[RESTRICTION_TYPE.OPEN_BOOKING] &&
          bw.restrictions[RESTRICTION_TYPE.OPEN_BOOKING][0];

        if (!openBooking || !openBooking.startDate || !openBooking.endDate) {
          return false;
        }

        return (
          moment(restriction.startDate).isSameOrBefore(moment(openBooking.endDate)) &&
          moment(openBooking.startDate).isSameOrBefore(moment(restriction.endDate))
        );
      });

      if (!overlapsWithAny) {
        return this.props.intl.formatMessage(messages.noOverlapWithBookingWindow, {
          start: restriction.startDate,
          end: restriction.endDate,
        });
      }
    }

    return "";
  };

  render() {
    const { config } = this.props;
    const { name, validation, fields, renderers, translation, entities } = config;
    const {
      locales,
      entity,
      entityError,
      entityResult,
      loading,
      getListPath,
      nested,
      afterSubmit,
      onCancelEdit,
      entityName,
    } = this.props;
    const { getFields, handleSubmit } = this;
    return (
      <EntityForm
        loading={loading}
        name={name}
        entityName={entityName}
        entity={entity}
        entities={entities}
        validation={validation}
        renderers={renderers}
        fields={getFields(fields)}
        locales={locales}
        translation={translation}
        onSubmit={handleSubmit}
        entityError={entityError}
        customValidate={this.customValidate}
        entityResult={entityResult}
        getListPath={getListPath}
        nested={nested}
        afterSubmit={afterSubmit}
        onCancelEdit={onCancelEdit}
        entityToFormValuesMapper={customEntityToFormValuesMapper}
      />
    );
  }
}

PromotionsCloseBookingWindowForm.propTypes = {};
PromotionsCloseBookingWindowForm.defaultProps = {};

export default injectIntl(PromotionsCloseBookingWindowForm);

